import i18n from '../../i18n';
import { readDocument } from '../../extensions/bundled/documents/documentsStore';
import { documentToMarkdown, exportFolderZip } from '../documentExport';
import { cmpNodes } from './treeModel';
import { zipStore } from './zipStore';

/** A file / folder name that every OS accepts. */
function safeName(name, fallback) {
  const clean = Array.from(String(name || ''), (ch) => (ch.charCodeAt(0) < 32 ? ' ' : ch))
    .join('')
    .replace(/[<>:"/\\|?*]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/[. ]+$/, '');
  return (clean || fallback).slice(0, 120);
}

/**
 * The folder's subtree as zip paths, depth-first in tree order: a folder is a
 * directory, a page is `<title>.md`, and a page's sub-pages go in a directory
 * of the page's name next to it. Names repeated in one directory get " (2)".
 * Pure — `nodes` is the flat `listTree` result.
 */
export function folderMarkdownPlan(nodes, folderId) {
  const byParent = new Map();
  for (const n of nodes || []) {
    const key = n.parentId ?? null;
    if (!byParent.has(key)) byParent.set(key, []);
    byParent.get(key).push(n);
  }
  for (const list of byParent.values()) list.sort(cmpNodes);
  const out = [];
  const seen = new Set();
  const walk = (parentId, dir) => {
    if (seen.has(parentId)) return;
    seen.add(parentId);
    const used = new Map();
    const unique = (base) => {
      const n = (used.get(base.toLowerCase()) || 0) + 1;
      used.set(base.toLowerCase(), n);
      return n === 1 ? base : `${base} (${n})`;
    };
    for (const node of byParent.get(parentId) || []) {
      if (node.type === 'folder') {
        walk(node.id, `${dir}${unique(safeName(node.name, i18n.t('docSidebar.untitledFolder')))}/`);
      } else {
        const base = unique(safeName(node.name, i18n.t('docSidebar.untitled')));
        out.push({ id: node.id, title: node.name || '', path: `${dir}${base}.md` });
        walk(node.id, `${dir}${base}/`);
      }
    }
  };
  walk(folderId, '');
  return out;
}

/** "Export folder…" → one zip of Markdown files. Resolves the save result, or `{ empty: true }`. */
export async function exportFolderMarkdown({ nodes, folder, scope }) {
  const plan = folderMarkdownPlan(nodes, folder.id);
  if (!plan.length) return { empty: true };
  const files = [];
  for (const entry of plan) {
    const doc = await readDocument(entry.id, scope); // eslint-disable-line no-await-in-loop
    files.push({ path: entry.path, content: documentToMarkdown(doc?.title || entry.title, doc?.bodyHtml || '') });
  }
  return exportFolderZip({ folderName: folder.name || i18n.t('docSidebar.untitledFolder'), bytes: zipStore(files) });
}
